// =============================================================================
// Painel White Label — Usuários dos Tenants
// Layout padronizado (ListPageLayout) com KPIs clicáveis, filtros rápidos +
// filtros avançados no Sheet "Mais" e PaginationFooter (50→500).
//
// @version 2.3.0
// RELEASE: 2026-05-30 v2.3.0 - Filtro rapido de Tenant movido para a barra
//                              principal usando WlTenantFilter (popover +
//                              busca server-side, padrao da auditoria WL).
//                              Sheet "Mais" e carregamento local de tenants
//                              removidos (nao havia outros filtros avancados).
// RELEASE: 2026-05-29 v2.2.0 - Listagem agora inclui usuários sem tenant
//                              (paridade com admin root). Coluna "Função"
//                              usa o mesmo design de badge da coluna
//                              "Cadastro" de Todos os Negócios. Coluna
//                              "Entrou em" renomeada para "Criado em" e
//                              passa a exibir hora (dd/MM/yyyy HH:mm).
//                              LGPD: e-mail e telefone exibidos mascarados
//                              com botão de reveal auditado.
// RELEASE: 2026-05-29 v2.1.0 - Coluna "Função" passa a exibir badges das
//                              roles reais (tenant_admin, agent, etc.) vindas
//                              do RPC wl_list_tenant_users v1.1.0. Removido
//                              o toggle "Ativo" — usuários de tenant não são
//                              gerenciados manualmente pelo painel WL.
// RELEASE: 2026-05-28 v2.0.0 - Migrado para o shell ListPageLayout/KpiCard.
// =============================================================================
import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import {
  Users,
  Search,
  ListChecks,
  CalendarPlus,
  CheckCircle2,
  XCircle,
  Crown,
  RefreshCw,
  Eye,
  EyeOff,
} from "lucide-react";
import {
  ListPageLayout,
  ListPageHeader,
  ListPageKpis,
  KpiCard,
  ListPageFiltersBar,
  LIST_FILTERS_INPUT_CLASS,
  LIST_FILTERS_SELECT_TRIGGER_CLASS,
  ListPageContent,
  ListPageFooter,
} from "@/components/layout/list-page";
import { PaginationFooter } from "@/components/ui/pagination-footer";
import { useWlTenantUserKpis } from "@/hooks/useWlListKpis";
import { useWlRevealTenantUserContact } from "@/hooks/useWlRevealTenantUserContact";
import { WlTenantFilter } from "@/components/wl/filters/WlTenantFilter";
import { cn } from "@/lib/utils";

/** Item de role retornado pelo RPC (jsonb_agg de {role}). */
interface WlTenantUserRole {
  role: string;
}

interface WlTenantUserRow {
  member_id: string | null;
  user_id: string;
  tenant_id: string | null;
  tenant_name: string | null;
  tenant_slug: string | null;
  /** Sempre NULL na listagem (revelar via RPC LGPD). */
  email: string | null;
  /** E-mail mascarado para exibição padrão. */
  email_masked: string | null;
  full_name: string | null;
  /** Sempre NULL na listagem (revelar via RPC LGPD). */
  phone: string | null;
  /** Telefone mascarado para exibição padrão. */
  phone_masked: string | null;
  is_owner: boolean;
  is_active: boolean;
  joined_at: string | null;
  /** Lista de roles do usuário dentro do tenant (vindo do RPC v1.1.0). */
  roles: WlTenantUserRole[] | null;
  total_count: number;
}


interface TenantOption { id: string; name: string; slug: string }

const PAGE_SIZE_OPTIONS = [50, 100, 200, 500];

/**
 * Cores das badges de role — mesma paleta usada no painel admin root
 * (src/pages/admin/Users.tsx) para manter consistência visual.
 */
const getRoleBadgeColor = (role: string) => {
  switch (role) {
    case "root_admin":
      return "bg-red-500/10 text-red-600 border-red-500/20";
    case "tenant_admin":
      return "bg-blue-500/10 text-blue-600 border-blue-500/20";
    case "agent":
      return "bg-green-500/10 text-green-600 border-green-500/20";
    case "owner_wl":
      return "bg-amber-500/10 text-amber-600 border-amber-500/20";
    case "admin_wl":
      return "bg-purple-500/10 text-purple-600 border-purple-500/20";
    default:
      return "bg-zinc-500/10 text-zinc-600 border-zinc-500/20";
  }
};

export default function WlTenantUsers() {
  const { toast } = useToast();

  const [rows, setRows] = useState<WlTenantUserRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  // tenantFilter: null = Todos (padrao WlTenantFilter)
  const [tenantFilter, setTenantFilter] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<string>("__all"); // __all | active | inactive
  /** KPI ativo: null | active | inactive | owners | joined_today. */
  const [kpiFilter, setKpiFilter] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);

  /** is_active efetivo: KPI active/inactive sobrescreve o Select. */
  const effectiveActive = useMemo<boolean | null>(() => {
    if (kpiFilter === "active") return true;
    if (kpiFilter === "inactive") return false;
    if (activeFilter === "active") return true;
    if (activeFilter === "inactive") return false;
    return null;
  }, [kpiFilter, activeFilter]);

  /** Filtros para KPI — não inclui o filtro que vem do card ativo. */
  const kpiFilters = useMemo(() => {
    const base: any = {
      tenant_id: tenantFilter,
      search: search || null,
      is_active: activeFilter === "active" ? true : activeFilter === "inactive" ? false : null,
    };
    if (kpiFilter === "active" || kpiFilter === "inactive") base.is_active = null;
    return base;
  }, [tenantFilter, search, activeFilter, kpiFilter]);

  const { data: kpis, isLoading: kpisLoading } = useWlTenantUserKpis(kpiFilters);

  // Reveal LGPD por user_id + campo (e-mail/telefone), auditado server-side
  const reveal = useWlRevealTenantUserContact();


  const fetchData = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc("wl_list_tenant_users", {
        p_tenant_id: tenantFilter,
        p_search: search || null,
        p_is_active: effectiveActive,
        p_limit: pageSize,
        p_offset: (page - 1) * pageSize,
      });
      if (error) throw error;
      let list = (data || []) as unknown as WlTenantUserRow[];
      // Filtro "owners" e "joined_today" são client-side (RPC não suporta nativamente)
      if (kpiFilter === "owners") list = list.filter((r) => r.is_owner);
      setRows(list);
      setTotal(list.length ? Number(list[0].total_count) : 0);
    } catch (e: any) {
      toast({ title: "Erro ao carregar usuários", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, tenantFilter, activeFilter, kpiFilter, search]);

  const toggleKpi = (key: string | null) => {
    setKpiFilter((prev) => (prev === key ? null : key));
    setPage(1);
  };

  

  return (
    <ListPageLayout>
      <ListPageHeader
        title="Usuários dos Tenants"
        description="Gestão cross-tenant de membros"
        actions={
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
          </Button>
        }
      />

      <ListPageKpis columns={5}>
        <KpiCard color="purple" icon={ListChecks} label="Total" value={kpisLoading ? "…" : kpis?.total ?? 0}
          onClick={() => toggleKpi(null)} active={kpiFilter === null} />
        <KpiCard color="cyan" icon={CalendarPlus} label="Entraram Hoje" value={kpisLoading ? "…" : kpis?.joined_today ?? 0}
          onClick={() => toggleKpi("joined_today")} active={kpiFilter === "joined_today"} />
        <KpiCard color="green" icon={CheckCircle2} label="Ativos" value={kpisLoading ? "…" : kpis?.active ?? 0}
          onClick={() => toggleKpi("active")} active={kpiFilter === "active"} />
        <KpiCard color="red" icon={XCircle} label="Inativos" value={kpisLoading ? "…" : kpis?.inactive ?? 0}
          onClick={() => toggleKpi("inactive")} active={kpiFilter === "inactive"} />
        <KpiCard color="yellow" icon={Crown} label="Owners" value={kpisLoading ? "…" : kpis?.owners ?? 0}
          onClick={() => toggleKpi("owners")} active={kpiFilter === "owners"} />
      </ListPageKpis>

      <ListPageFiltersBar isFetching={loading}>
        <div className="relative flex-1 min-w-[200px] sm:flex-none sm:w-[260px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
          <Input
            className={cn(LIST_FILTERS_INPUT_CLASS, "pl-7 sm:pl-8")}
            placeholder="Nome, e-mail ou tenant…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { setSearch(searchInput); setPage(1); } }}
            onBlur={() => { if (searchInput !== search) { setSearch(searchInput); setPage(1); } }}
          />
        </div>
        <WlTenantFilter
          value={tenantFilter}
          onChange={(id) => { setTenantFilter(id); setPage(1); }}
        />
        <Select value={activeFilter} onValueChange={(v) => { setActiveFilter(v); setKpiFilter(null); setPage(1); }}>
          <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, "sm:w-[140px]")}>
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">Todos status</SelectItem>
            <SelectItem value="active">Ativos</SelectItem>
            <SelectItem value="inactive">Inativos</SelectItem>
          </SelectContent>
        </Select>
      </ListPageFiltersBar>

      <ListPageContent fullBleed>
        {loading ? (
          <p className="p-4 text-sm text-muted-foreground">Carregando…</p>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
            <Users className="h-10 w-10 opacity-40" />
            Nenhum usuário encontrado
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs font-medium">Usuário</TableHead>
                <TableHead className="text-xs font-medium">E-mail</TableHead>
                <TableHead className="text-xs font-medium">Telefone</TableHead>
                <TableHead className="text-xs font-medium">Tenant</TableHead>
                <TableHead className="text-xs font-medium">Função</TableHead>
                <TableHead className="text-xs font-medium">Criado em</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => {
                // Normaliza roles (RPC devolve [] mesmo quando vazio, mas
                // defensivo contra null/undefined em respostas antigas).
                const roleList = Array.isArray(r.roles) ? r.roles : [];
                // Reveal LGPD por campo (cache local no hook)
                const emailRevealed = reveal.get(r.user_id, "email");
                const phoneRevealed = reveal.get(r.user_id, "phone");
                // Chave única — usa member_id quando vinculado a tenant,
                // senão combina user_id (linha "sem tenant" do user)
                const rowKey = r.member_id ?? `nouser-${r.user_id}`;
                return (
                  <TableRow key={rowKey}>
                    <TableCell className="py-2 text-xs font-medium">{r.full_name || "—"}</TableCell>

                    {/* E-mail mascarado + reveal auditado */}
                    <TableCell className="py-2 text-xs">
                      {r.email_masked ? (
                        <div className="inline-flex items-center gap-1.5">
                          <span className="font-mono text-[11px] text-foreground">
                            {emailRevealed ?? r.email_masked}
                          </span>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-5 w-5"
                            disabled={reveal.isRevealing(r.user_id, "email")}
                            onClick={() =>
                              emailRevealed
                                ? reveal.hide(r.user_id, "email")
                                : reveal.reveal(r.user_id, "email")
                            }
                            title={emailRevealed ? "Ocultar e-mail" : "Revelar e-mail (auditado)"}
                          >
                            {emailRevealed ? (
                              <EyeOff className="h-3 w-3" />
                            ) : (
                              <Eye className="h-3 w-3" />
                            )}
                          </Button>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>

                    {/* Telefone mascarado + reveal auditado */}
                    <TableCell className="py-2 text-xs">
                      {r.phone_masked ? (
                        <div className="inline-flex items-center gap-1.5">
                          <span className="font-mono text-[11px] text-foreground">
                            {phoneRevealed ?? r.phone_masked}
                          </span>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-5 w-5"
                            disabled={reveal.isRevealing(r.user_id, "phone")}
                            onClick={() =>
                              phoneRevealed
                                ? reveal.hide(r.user_id, "phone")
                                : reveal.reveal(r.user_id, "phone")
                            }
                            title={phoneRevealed ? "Ocultar telefone" : "Revelar telefone (auditado)"}
                          >
                            {phoneRevealed ? (
                              <EyeOff className="h-3 w-3" />
                            ) : (
                              <Eye className="h-3 w-3" />
                            )}
                          </Button>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>

                    <TableCell className="py-2 text-xs">
                      {r.tenant_name ? (
                        <>
                          <div className="text-foreground">{r.tenant_name}</div>
                          <div className="text-[11px] text-muted-foreground">{r.tenant_slug}</div>
                        </>
                      ) : (
                        <span className="text-muted-foreground">Sem tenant</span>
                      )}
                    </TableCell>

                    {/* Função — badge idêntico ao da coluna "Cadastro" de
                        Todos os Negócios (DealOriginBadge): text-[10px],
                        px-2 py-0.5, border-transparent. */}
                    <TableCell className="py-2">
                      <div className="flex flex-wrap gap-1">
                        {roleList.length === 0 ? (
                          <Badge
                            variant="outline"
                            className="inline-flex items-center gap-1 border-transparent text-[10px] font-medium px-2 py-[1px] leading-tight bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300"
                          >
                            Sem role
                          </Badge>
                        ) : (
                          roleList.map((ur, idx) => (
                            <Badge
                              key={`${rowKey}-${idx}`}
                              variant="outline"
                              className={cn(
                                "inline-flex items-center gap-1 border-transparent text-[10px] font-medium px-2 py-[1px] leading-tight",
                                getRoleBadgeColor(ur.role),
                              )}
                            >
                              {ur.role}
                            </Badge>
                          ))
                        )}
                      </div>
                    </TableCell>

                    <TableCell className="py-2 text-xs text-muted-foreground whitespace-nowrap">
                      {r.joined_at ? format(new Date(r.joined_at), "dd/MM/yyyy HH:mm") : "—"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </ListPageContent>


      <ListPageFooter>
        <PaginationFooter
          totalItems={total}
          currentPage={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
      </ListPageFooter>

    </ListPageLayout>
  );
}
